/**
 * Who may edit an event, shared by web and native.
 *
 * Both apps ask this question of the same `CalendarEvent` and must answer it
 * identically — a surface that lets an edit through on one platform and refuses
 * it on the other is a bug on whichever side is wrong. Kept here rather than
 * copied per platform because a one-sided change would ship green: each package
 * tests only its own copy.
 *
 * The predicate that decides what counts as a reminder lives next door in
 * `reminder.ts`. Platform-specific gates layer on top of this in each app —
 * web's drag check adds calendar writability and in-flight-sync tests, which
 * depend on web-only types.
 */

import type { CalendarEvent } from "@/types/events.ts";
import { isReminderEvent } from "./reminder.ts";

type ParticipantLike = { self?: boolean; role?: string } | undefined;

/**
 * Returns true when the current user is allowed to edit this event from the
 * detail surface: organizer, or `guestsCanModify` set, or sole attendee — and
 * the event has a provider id and is not cancelled.
 *
 * A reminder answers none of those and is still the user's to change. It is
 * connectorless, so it has no provider id at all, and the people on it are
 * recipients rather than guests — nobody replies to a reminder, so there is no
 * organizer among them. Its only gate is cancellation.
 *
 * Named for web's sidebar, where the check originated, so the predicate stays
 * greppable across both platforms.
 */
export function canEditEventInSidebar(event: CalendarEvent): boolean {
  if (event.status === "cancelled") return false;
  if (isReminderEvent(event)) return true;

  const selfParticipant: ParticipantLike = event.participants?.find((p) => p.self);
  const isOrganizer = selfParticipant?.role === "organizer";
  const hasNoParticipants = !event.participants || event.participants.length === 0;
  return (isOrganizer || !!event.guestsCanModify || hasNoParticipants) && !!event.externalEventId;
}

/**
 * Google's grants that let a user see a calendar's events but not write to
 * them — including the self-response an RSVP patches.
 */
const READ_ONLY_ACCESS_ROLES = new Set(["reader", "freeBusyReader"]);

/**
 * Whether the user may RSVP to events on a calendar with this access role.
 *
 * Refuses only a role known to be read-only. An RSVP there is accepted, saved
 * as "attending", then rejected by Google with a 403 — and nothing rolls the
 * card back (BIG-7628). Every other role, including none, keeps the controls:
 * Outlook calendars report no role at all, and treating that as read-only
 * would take RSVP away from every Outlook event. The same holds while the
 * calendar list is still loading.
 */
export function canRsvpOnCalendar(accessRole: string | null | undefined): boolean {
  return !accessRole || !READ_ONLY_ACCESS_ROLES.has(accessRole);
}
